// // src/services/authService.ts

// import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// import {
//   LoginCredentials,
//   LoginResponse,
//   AuthState,
// } from "../types/auth.types";
// import { TokenManager } from "../utils/tokenManager";

// // Environment configuration - exclude production
// const getApiBaseUrl = (): string => {
//   const viteEnv = import.meta.env.VITE_ENVIRONMENT;

//   switch (viteEnv) {
//     case "staging":
//       return "https://api.staging.myautochek.com";
//     case "development":
//     default:
//       return "https://api-dev.autochek.africa";
//   }
// };

// const BASE_URL = getApiBaseUrl();

// console.log("🔐 Using Auth API Base URL:", BASE_URL);

// class AuthService {
//   async login(credentials: LoginCredentials): Promise<LoginResponse> {
//     try {
//       console.log("🚀 Attempting login for:", credentials.email);

//       // Change the api to the new authentication /v2/users/login/ endpoint
//       const response = await fetch(`${BASE_URL}/v1/auth/login`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Accept: "application/json",
//         },
//         body: JSON.stringify(credentials),
//       });

//       if (!response.ok) {
//         const errorData = await response
//           .json()
//           .catch(() => ({ message: "Login failed" }));
//         throw new Error(
//           errorData.message ||
//             `Login failed: ${response.status} - ${response.statusText}`,
//         );
//       }

//       // This credentials will change to username and password in the future, but for now we will keep it as email and password
//       const data: LoginResponse = await response.json();
//       console.log("✅ Login successful:", {
//         email: credentials.email,
//         userId: data.user.id,
//       });

//       // we need to set the token and set access token

//       // Store token and user data
//       TokenManager.setToken(data.token);
//       TokenManager.setUserData(data.user, data.authority);

//       return data;
//     } catch (error) {
//       console.error("❌ Login failed:", error);
//       throw error;
//     }
//   }

//   logout(): void {
//     console.log("🔓 Logging out user");
//     TokenManager.clearToken();
//   }

//   getCurrentAuthState(): AuthState {
//     const token = TokenManager.getToken();
//     const userData = TokenManager.getUserData();

//     if (!token || !userData) {
//       return {
//         user: null,
//         token: null,
//         isAuthenticated: false,
//         authority: null,
//       };
//     }

//     return {
//       user: userData.user,
//       token,
//       isAuthenticated: true,
//       authority: userData.authority,
//     };
//   }

//   isAuthenticated(): boolean {
//     return TokenManager.isTokenValid();
//   }
// }

// export const authService = new AuthService();

// // React Query hooks
// export const useLogin = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: (credentials: LoginCredentials) =>
//       authService.login(credentials),
//     onSuccess: (data) => {
//       // Invalidate and refetch any cached data after successful login
//       queryClient.invalidateQueries();
//     },
//     onError: (error) => {
//       console.error("Login mutation failed:", error);
//     },
//   });
// };

// export const useAuth = () => {
//   return useQuery({
//     queryKey: ["auth"],
//     queryFn: () => authService.getCurrentAuthState(),
//     staleTime: 60000, // 1 minute
//     // cacheTime: 300000, // 5 minutes
//     refetchInterval: 60000, // Check every minute for token expiry
//   });
// };

// export const useLogout = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: () => {
//       authService.logout();
//       return Promise.resolve();
//     },
//     onSuccess: () => {
//       // Clear all cached data after logout
//       queryClient.clear();
//       window.location.reload();
//     },
//   });
// };

// src/services/authService.ts

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoginCredentials, AuthState } from "../types/auth.types";
import { TokenManager } from "../utils/tokenManager";

// ─────────────────────────────────────────────
// Base URL config (unchanged from your original)
// ─────────────────────────────────────────────
// const getApiBaseUrl = (): string => {
//   const viteEnv = import.meta.env.VITE_ENVIRONMENT;
//   switch (viteEnv) {
//     case "staging":
//       return "https://api.staging.myautochek.com";
//     case "development":
//     default:
//       return "https://api-dev.autochek.africa";
//   }
// };

const BASE_URL = import.meta.env.VITE_BASE_URL
const APP_NAME = "zoho"; // The x-autochek-app header value — required on all V2 calls

console.log("🔐 Using Auth API Base URL:", BASE_URL);

class AuthService {
  // ─────────────────────────────────────────────
  // LOGIN
  //
  // V1 flow (old):
  //   POST /v1/auth/login  →  get { token, user, authority } all in one go
  //
  // V2 flow (new):
  //   Step 1: POST /v2/users/login  →  get { accessToken, refreshToken }
  //   Step 2: GET  /v2/users/authority  →  get roles/permissions
  //   Step 3: GET  /v2/users/me  →  get user profile
  // ─────────────────────────────────────────────
  async login(credentials: LoginCredentials): Promise<void> {
    console.log("🚀 Attempting V2 login for:", credentials.username);

    // ── Step 1: Login ──────────────────────────
    const loginRes = await fetch(`${BASE_URL}/v2/users/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-autochek-app": APP_NAME, // Required in V2 — was not needed in V1
        "Cache-Control": "no-cache",
        // ⚠️ Do NOT add Authorization header here — login endpoint doesn't want it
      },
      body: JSON.stringify({
        username: credentials.username, // V2 calls it "username" (accepts email or phone)
        password: credentials.password,
      }),
    });

    const loginData = await loginRes.json();

    if (!loginRes.ok) {
      // V2 error messages are user-friendly so we can show them directly
      throw new Error(loginData.message || `Login failed: ${loginRes.status}`);
    }

    // Handle "password change required" — V2 returns 403 for this
    if (loginRes.status === 403) {
      const redirectUrl = encodeURIComponent(window.location.href);
      const accountsUrl =
        import.meta.env.VITE_ENVIRONMENT === "staging"
          ? `https://accounts.staging.myautochek.com/?redirectUrl=${redirectUrl}`
          : `https://accounts.dev.myautochek.com/?redirectUrl=${redirectUrl}`;
      window.location.href = accountsUrl;
      return;
    }

    const { accessToken, refreshToken } = loginData.data;

    // Store both tokens
    TokenManager.setAccessToken(accessToken);
    TokenManager.setRefreshToken(refreshToken);

    // ── Step 2: Fetch roles/permissions ───────
    // In V1 these came with the login response. In V2 you fetch them separately.
    try {
      const authorityRes = await fetch(`${BASE_URL}/v2/users/authority`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-autochek-app": APP_NAME,
        },
      });
      if (authorityRes.ok) {
        const authorityData = await authorityRes.json();
        const roles: string[] = authorityData.data?.roles ?? [];

        // ── Role gate ──────────────────────────────────────────
        // Only DASHBOARD_ADMIN or DASHBOARD_USER may access this app.
        // If neither role is present, invalidate the server session
        // immediately and reject the login attempt.
        const ALLOWED_ROLES = ["DASHBOARD_ADMIN", "DASHBOARD_USER"];
        const hasAccess = roles.some((r) => ALLOWED_ROLES.includes(r));

        if (!hasAccess) {
          // Tell the server to invalidate the session
          await fetch(`${BASE_URL}/v2/users/logout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              "x-autochek-app": APP_NAME,
            },
            body: JSON.stringify({ refreshToken, all: false }),
          }).catch(() => {}); // Non-blocking — don't let a network error hide the real message

          TokenManager.clearAll();
          throw new Error(
            "Access denied. Your account does not have permission to access this dashboard. Please contact your administrator."
          );
        }

        TokenManager.setAuthority(authorityData.data);
      }
    } catch (err) {
      // Re-throw access-denied errors so the login form can display them
      if (err instanceof Error && err.message.startsWith("Access denied")) throw err;
      // Otherwise non-fatal — app can work without roles if this fails
      console.warn("⚠️ Could not fetch user authority:", err);
    }

    // ── Step 3: Fetch user profile ─────────────
    // In V1 the user object came with the login response. In V2 you fetch it separately.
    try {
      const profileRes = await fetch(`${BASE_URL}/v2/users/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "x-autochek-app": APP_NAME,
        },
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        TokenManager.setUserData(profileData.data);
      }
    } catch (err) {
      // Non-fatal — app can work without full profile
      console.warn("⚠️ Could not fetch user profile:", err);
    }

    console.log("✅ V2 Login successful");
  }

  // ─────────────────────────────────────────────
  // LOGOUT
  //
  // V1: just clear localStorage — no API call needed
  // V2: you must call the API to invalidate the refresh token on the server,
  //     THEN clear localStorage
  // ─────────────────────────────────────────────
  async logout(): Promise<void> {
    console.log("🔓 Logging out user");

    const accessToken = TokenManager.getToken();
    const refreshToken = TokenManager.getRefreshToken();

    // Tell the server to invalidate this session
    if (accessToken && refreshToken) {
      try {
        await fetch(`${BASE_URL}/v2/users/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "x-autochek-app": APP_NAME,
          },
          body: JSON.stringify({
            refreshToken,
            all: false, // true would log out from ALL devices
          }),
        });
      } catch (err) {
        // Don't block the user from logging out if the API call fails
        console.warn("⚠️ Logout API call failed, clearing tokens anyway:", err);
      }
    }

    // Always clear local storage regardless of API result
    TokenManager.clearAll();
  }

  // ─────────────────────────────────────────────
  // GET CURRENT AUTH STATE
  // Reads from localStorage — no API call
  // Used by ProtectedRoute to decide whether to show the app or the login form
  // ─────────────────────────────────────────────
  getCurrentAuthState(): AuthState {
    const token = TokenManager.getToken();
    const user = TokenManager.getUserData();
    const authority = TokenManager.getAuthority();
    const refreshToken = TokenManager.getRefreshToken();

    if (!user) {
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        authority: null,
      };
    }

    // Consider the session alive if:
    //   a) the access token is still valid, OR
    //   b) we have a refresh token that can obtain a new access token.
    // The API layer (makeRequest) will silently refresh on the next 401,
    // so we never kick the user to the login page just because the
    // short-lived access token expired.
    const isAuthenticated = TokenManager.isTokenValid() || !!refreshToken;

    return {
      user,
      token,
      isAuthenticated,
      authority,
    };
  }

  isAuthenticated(): boolean {
    return TokenManager.isTokenValid();
  }
}

export const authService = new AuthService();

// ─────────────────────────────────────────────
// REACT QUERY HOOKS (same API as before — components don't need to change)
// ─────────────────────────────────────────────

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      authService.login(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error("Login mutation failed:", error);
    },
  });
};

export const useAuth = () => {
  return useQuery({
    queryKey: ["auth"],
    queryFn: () => authService.getCurrentAuthState(),
    staleTime: 60000,
    refetchInterval: 60000,
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(), // Now async — calls the API
    onSuccess: () => {
      queryClient.clear();
      window.location.reload();
    },
  });
};
